import { Box, Divider, Paper, Typography } from '@mui/material'
import { Theme, useTheme } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles/makeStyles'
import React from 'react'
import {
  Area,
  Bar,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

interface AlertCountGraphProps {
  data: React.ComponentProps<typeof ComposedChart>['data']
  loading: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  graphContent: {
    height: '500px',
    fontFamily: theme.typography.body2.fontFamily,
  },
  bar: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
}))


type Visibility = {
  total: boolean
  closed: boolean
  acked: boolean
  escalated: boolean
  noisy: boolean
}

import { ColoredCheckbox } from './ColoredCheckbox'

export default function AlertCountGraph(
  props: AlertCountGraphProps,
): JSX.Element {
  const classes = useStyles()
  const theme = useTheme()
    const isLight = theme.palette.mode === 'light'


  const [visibility, setVisibility] = React.useState({
    total: true,
    closed: true,
    acked: true,
    escalated: true,
    noisy: true,
  })

  const toggle = (key: keyof Visibility) => {
    setVisibility(v => ({ ...v, [key]: !v[key] }))
  }

  return (  <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: isLight
          ? theme.palette.grey[50]
          : theme.palette.background.paper,
        boxShadow: isLight ? theme.shadows[1] : 'none',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          backgroundColor: isLight
            ? theme.palette.grey[100]
            : theme.palette.background.default,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            flexWrap: 'wrap',
          }}
        >
          <ColoredCheckbox
            label="Total Alerts"
            color={theme.palette.primary.main}
            checked={visibility.total}
            onToggle={() => toggle('total')}
          />
          <ColoredCheckbox
            label="Closed"
            color={theme.palette.success.main}
            checked={visibility.closed}
            onToggle={() => toggle('closed')}
          />
          <ColoredCheckbox
            label="Acknowledged"
            color={theme.palette.secondary.main}
            checked={visibility.acked}
            onToggle={() => toggle('acked')}
          />
          <ColoredCheckbox
            label="Escalated"
            color={theme.palette.info.main}
            checked={visibility.escalated}
            onToggle={() => toggle('escalated')}
          />
          <ColoredCheckbox
            label="Noisy"
            color={theme.palette.warning.main}
            checked={visibility.noisy}
            onToggle={() => toggle('noisy')}
          />
        </Box>
      </Box>

      <Divider />
        <Box
        sx={{
          px: 2,
          py: 2,
          backgroundColor: isLight
            ? theme.palette.common.white
            : theme.palette.background.paper,
        }}
      >
        <Box sx={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={props.data}>
              <XAxis
                dataKey='date'
                type='category'
                stroke={theme.palette.text.secondary}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              />
              <YAxis
                allowDecimals={false}
                dataKey='count'
                stroke={theme.palette.text.secondary}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              />
              <Tooltip
                data-cy='metrics-tooltip'
                cursor={{ fill: theme.palette.background.default }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null

                  return (
                    <Paper variant='outlined' sx={{ p: 1 }}>
                      <Typography variant='body2'>
                        {payload[0].payload.label}
                      </Typography>
                      {payload.map((p: any) => (
                        <Typography key={p.name} variant='body2'>
                          {p.name}: {p.value}
                        </Typography>
                      ))}
                    </Paper>
                  )
                }}
              />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              <Bar
                stackId='a'
                dataKey='count'
                fillOpacity={props.loading ? 0.5 : 1}
                fill={
                  theme.palette.mode === 'light'
                    ? theme.palette.secondary.dark
                    : theme.palette.secondary.light
                }
                className={classes.bar}
                name='Total Alerts'
                hide={!visibility.total}
              />
              <Area
                dataKey='closedCount'
                type='monotone'
                stroke={
                  theme.palette.mode === 'light'
                    ? theme.palette.success.dark
                    : theme.palette.success.light
                }
                fill={
                  theme.palette.mode === 'light'
                    ? theme.palette.success.light
                    : theme.palette.success.dark
                }
                name='Closed'
                hide={!visibility.closed}
              />
              <Area
                dataKey='acknowledgedCount'
                type='monotone'
                stroke={
                  theme.palette.mode === 'light'
                    ? theme.palette.primary.dark
                    : theme.palette.primary.light
                }
                fill={
                   theme.palette.mode === 'light'
                    ? theme.palette.primary.light
                    : theme.palette.primary.dark
                }
                name='Acknowledged'
                hide={!visibility.acked}
              />
              <Area
                dataKey='escalatedCount'
                type='monotone'
                stroke={
                  theme.palette.mode === 'light'
                    ? theme.palette.info.dark
                    : theme.palette.info.light
                }
                fill={
                  theme.palette.mode === 'light'
                    ? theme.palette.info.light
                    : theme.palette.info.dark
                }
                name='Escalated'
                hide={!visibility.escalated}
              />
              <Area
                dataKey='noiseCount'
                type='monotone'
                stroke={
                  theme.palette.mode === 'light'
                    ? theme.palette.warning.dark
                    : theme.palette.warning.light
                }
                fill={
                  theme.palette.mode === 'light'
                    ? theme.palette.warning.light
                    : theme.palette.warning.dark
                }
                name='Noisy'
                hide={!visibility.noisy}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  )
}
